import { useCallback } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Checkbox, type CheckboxProps } from '@opendoor/atoms/checkbox';
import { Combobox, type ComboboxOption } from '@opendoor/atoms/combobox';
import { Grid } from '@opendoor/atoms/grid';
import { Text } from '@opendoor/atoms/text';

import { buildRangeFilterOptions } from './utils';

const RoomSizeOptions = [
  { label: '1+', value: '1' },
  { label: '2+', value: '2' },
  { label: '3+', value: '3' },
  { label: '4+', value: '4' },
];

const BEDS_PLACEHOLDER = { label: 'Min beds', value: '0' };
const BATHS_PLACEHOLDER = { label: 'Min baths', value: '0' };
const BEDS_OPTIONS = buildRangeFilterOptions(RoomSizeOptions, BEDS_PLACEHOLDER);
const BATHS_OPTIONS = buildRangeFilterOptions(RoomSizeOptions, BATHS_PLACEHOLDER);
const ROOM_FILTER_GRID_CLASS = 'grid-cols-[minmax(0,1fr)_9.5rem] items-center gap-x-4 gap-y-3';

export interface BedroomsBathroomsFilterProps {
  minBaths?: string | null;
  minBeds?: string | null;
  maxBeds?: string | null;
  handleMinBathsChange: (value: string) => void;
  handleBedroomsChange: (value: BedroomFilterValue) => void;
}

export interface BedroomFilterValue {
  minBeds: string;
  maxBeds: string;
}

export const BedroomsBathroomsFilter = ({
  minBaths,
  minBeds,
  maxBeds,
  handleMinBathsChange,
  handleBedroomsChange,
}: BedroomsBathroomsFilterProps) => {
  const normalizedMinBeds = minBeds || BEDS_PLACEHOLDER.value;
  const exactBedrooms =
    normalizedMinBeds !== BEDS_PLACEHOLDER.value && maxBeds === normalizedMinBeds;

  const handleMinBedsChange = useCallback(
    (option: ComboboxOption | null) => {
      const value = option?.value ?? BEDS_PLACEHOLDER.value;
      handleBedroomsChange({
        minBeds: value,
        maxBeds: exactBedrooms ? value : BEDS_PLACEHOLDER.value,
      });
    },
    [exactBedrooms, handleBedroomsChange],
  );

  const handleExactBedroomsChange = useCallback<NonNullable<CheckboxProps['onCheckedChange']>>(
    (checked) => {
      handleBedroomsChange({
        minBeds: normalizedMinBeds,
        maxBeds: checked ? normalizedMinBeds : BEDS_PLACEHOLDER.value,
      });
    },
    [handleBedroomsChange, normalizedMinBeds],
  );

  const handleMinBathsOptionChange = useCallback(
    (option: ComboboxOption | null) => {
      handleMinBathsChange(option?.value ?? BATHS_PLACEHOLDER.value);
    },
    [handleMinBathsChange],
  );

  return (
    <Col className="w-full gap-6">
      <Text as="p" className="text-label-xl">
        Beds/Baths
      </Text>
      <Col className="gap-6">
        <Grid className={ROOM_FILTER_GRID_CLASS}>
          <Text as="p" className="text-body-md">
            Beds
          </Text>
          <Box className="w-full">
            <Combobox
              aria-label="Minimum bedrooms"
              placeholder={BEDS_PLACEHOLDER.label}
              options={BEDS_OPTIONS}
              value={BEDS_OPTIONS.find((option) => option.value === minBeds) ?? null}
              size="sm"
              onValueChange={handleMinBedsChange}
            />
          </Box>
          <Box className="col-start-2 w-full justify-start">
            <Checkbox
              analyticsName="marketplace-exact-bedrooms-filter"
              caption="Exact bedrooms"
              checked={exactBedrooms}
              disabled={normalizedMinBeds === BEDS_PLACEHOLDER.value}
              size="xs"
              onCheckedChange={handleExactBedroomsChange}
            />
          </Box>
        </Grid>
        <Grid className={ROOM_FILTER_GRID_CLASS}>
          <Text as="p" className="text-body-md">
            Baths
          </Text>
          <Box className="w-full">
            <Combobox
              aria-label="Minimum bathrooms"
              placeholder={BATHS_PLACEHOLDER.label}
              options={BATHS_OPTIONS}
              value={BATHS_OPTIONS.find((option) => option.value === minBaths) ?? null}
              size="sm"
              onValueChange={handleMinBathsOptionChange}
            />
          </Box>
        </Grid>
      </Col>
    </Col>
  );
};
